import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import { createContext } from "react";
import { UserState, userState } from "./state/UserState";
import apiClient from "./api/pe-api-client";
import "@playerelite/ui/themes/all-brands.css";
import App from "./app";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useEffect } from "react";
import {
  isProductionUrl,
  setDocumentTitle,
  setFavicon,
  setMetaDescription,
} from "./utilities";

// Initialize Sentry

Sentry.init({
  dsn: "https://d8e34ab5c8484da44c58ca33a0e4036b@o4505238491693056.ingest.us.sentry.io/4508249712689152",
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

export enum VENDORS {
  USA = "USA",
  PE = "PE",
  ODYSSEY = "ODYSSEY",
}

export const VENDOR = process.env.REACT_APP_VENDOR ?? VENDORS.PE;

// Scope @playerelite/ui tokens to the vendor's brand (defined in all-brands.css).
// PE/USA stay on the :root defaults, which are the Player Elite tokens: setting
// data-brand="playerelite" would also apply the kit's light-mode --pe-primary
// correction and darken every kit primary button.
if (VENDOR === VENDORS.ODYSSEY) {
  document.documentElement.dataset.brand = "odyssey";
}

export const UserStore = createContext<UserState>(userState);
console.log("VENDOR => ", VENDOR);

// Set favicon based on VENDOR type
if (VENDOR === VENDORS.ODYSSEY) {
  setFavicon("/od_favicon.ico");
  setDocumentTitle("NEXUS - Odyssey Command Centre"); // Example title for Odyssey
  setMetaDescription("NEXUS - Odyssey Command Centre"); // Example description
}

const prodApiBaseUrl =
  VENDOR === VENDORS.ODYSSEY
    ? "https://od-cc-api-prod.playerelite-dev.com"
    : VENDOR === VENDORS.USA
    ? "https://cc-api.usa-prod.playereliteapps.us"
    : "https://cc-api-prod.playerelite-dev.com";

// Only PE has a staging API so far; USA and Odyssey use prod everywhere.
const stagingApiBaseUrl =
  VENDOR === VENDORS.PE
    ? "https://cc-api-staging.playerelite-dev.com"
    : prodApiBaseUrl;

// Production hosts always use the prod API. Everywhere else (staging, localhost)
// uses staging, unless REACT_APP_API_BASE_URL overrides it.
export const apiBaseUrl = isProductionUrl()
  ? prodApiBaseUrl
  : process.env.REACT_APP_API_BASE_URL || stagingApiBaseUrl;

console.log("loading env => ", VENDOR);
console.log("apiBaseUrl => ", apiBaseUrl);

// Wire up the axios interceptor, then validate any cached session against the
// server before the first render. App gates on userState.authChecked so we never
// show authenticated UI off a stale/expired token.
apiClient.init(userState);
userState.verifyAuth();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<App />);
